/**
 * Minimal XLSX exporter - creates valid Excel files without heavy dependencies.
 *
 * XLSX files are ZIP archives containing XML files:
 * - [Content_Types].xml - declares content types
 * - _rels/.rels - root relationships
 * - xl/workbook.xml - workbook definition
 * - xl/_rels/workbook.xml.rels - workbook relationships
 * - xl/worksheets/sheet1.xml - the actual data
 * - xl/styles.xml - cell styles
 */

import JSZip from 'jszip';

export type CellType = 'string' | 'number' | 'date' | 'money';
export type CellValue = string | number | Date | null | undefined;
export type Row = Array<{ value: CellValue; type?: CellType }>;

interface XlsxConfig {
    filename: string;
    sheet: {
        data: Row[];
    };
}

// Excel epoch is 1900-01-01, but Excel incorrectly thinks 1900 was a leap year
// So we need to account for that bug
const EXCEL_EPOCH = new Date(1899, 11, 30).getTime();
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function dateToExcelSerial(date: Date): number {
    return (date.getTime() - EXCEL_EPOCH) / MS_PER_DAY;
}

function escapeXml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

function columnIndexToLetter(index: number): string {
    let letter = '';
    while (index >= 0) {
        letter = String.fromCharCode((index % 26) + 65) + letter;
        index = Math.floor(index / 26) - 1;
    }
    return letter;
}

function generateContentTypes(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>
</Types>`;
}

function generateRels(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;
}

function generateWorkbook(): string {
    // Window size: 1200x800 pixels converted to twips (1 pixel = 15 twips)
    const windowWidth = 1200 * 15;  // 18000 twips
    const windowHeight = 800 * 15; // 12000 twips

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <bookViews>
    <workbookView xWindow="0" yWindow="0" windowWidth="${windowWidth}" windowHeight="${windowHeight}" activeTab="0"/>
  </bookViews>
  <sheets>
    <sheet name="Sheet1" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`;
}

function generateWorkbookRels(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="sharedStrings.xml"/>
</Relationships>`;
}

function generateStyles(): string {
    // Style indexes:
    // 0 = normal
    // 1 = bold (header)
    // 2 = date format (yyyy-mm-dd)
    // 3 = money format ($#,##0.00)
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="2">
    <numFmt numFmtId="164" formatCode="yyyy-mm-dd"/>
    <numFmt numFmtId="165" formatCode="&quot;$&quot;#,##0.00"/>
  </numFmts>
  <fonts count="2">
    <font><sz val="11"/><name val="Calibri"/></font>
    <font><b/><sz val="11"/><name val="Calibri"/></font>
  </fonts>
  <fills count="2">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
  </fills>
  <borders count="1">
    <border><left/><right/><top/><bottom/><diagonal/></border>
  </borders>
  <cellStyleXfs count="1">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0"/>
  </cellStyleXfs>
  <cellXfs count="4">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
    <xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
    <xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
  </cellXfs>
</styleSheet>`;
}

function generateSharedStrings(strings: string[]): string {
    const uniqueStrings = [...new Set(strings)];
    const stringItems = uniqueStrings.map(s => `<si><t>${escapeXml(s)}</t></si>`).join('');
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${strings.length}" uniqueCount="${uniqueStrings.length}">
${stringItems}
</sst>`;
}

/**
 * Calculate the display width of a cell value for auto-sizing columns.
 * Returns width in Excel character units (roughly the width of '0' in default font).
 */
function getCellDisplayWidth(value: CellValue, type?: CellType): number {
    if (value === null || value === undefined || value === '') return 0;

    if (value instanceof Date || type === 'date') {
        // Date format: yyyy-mm-dd = 10 characters
        return 10;
    }

    if (type === 'money') {
        // Money format: $#,##0.00 - estimate width with currency symbol and commas
        const numValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
        // Format: $ + digits + commas + .00
        const formatted = numValue.toLocaleString('en-US', {style: 'currency', currency: 'USD'});
        return formatted.length;
    }

    if (typeof value === 'number' || type === 'number') {
        // Format number and measure
        const numStr = typeof value === 'number' ? value.toString() : String(value);
        return numStr.length;
    }

    // String - measure the actual content
    return String(value).length;
}

/**
 * Calculate optimal column widths based on content.
 * Returns array of widths in Excel character units.
 */
function calculateColumnWidths(data: Row[]): number[] {
    if (data.length === 0) return [];

    const maxCols = Math.max(...data.map(row => row.length));
    const widths: number[] = new Array(maxCols).fill(0);

    data.forEach((row, rowIndex) => {
        row.forEach((cell, colIndex) => {
            let width = getCellDisplayWidth(cell.value, cell.type);

            // Add padding for header row (bold text is slightly wider)
            if (rowIndex === 0) width += 2;

            // Track maximum width per column
            widths[colIndex] = Math.max(widths[colIndex], width);
        });
    });

    // Apply min/max constraints and add padding
    return widths.map(w => {
        const padded = w + 2; // Add padding
        return Math.max(8, Math.min(padded, 60)); // Min 8, max 60 characters
    });
}

/**
 * Generate column definitions XML for auto-sized columns.
 */
function generateColumns(widths: number[]): string {
    if (widths.length === 0) return '';

    const cols = widths.map((width, index) => {
        const colNum = index + 1;
        return `<col min="${colNum}" max="${colNum}" width="${width}" bestFit="1" customWidth="1"/>`;
    }).join('\n    ');

    return `<cols>\n    ${cols}\n  </cols>`;
}

function generateSheet(data: Row[], sharedStrings: Map<string, number>): string {
    let rows = '';

    // Calculate column widths for auto-sizing
    const columnWidths = calculateColumnWidths(data);
    const colsXml = generateColumns(columnWidths);

    data.forEach((row, rowIndex) => {
        let cells = '';
        row.forEach((cell, colIndex) => {
            const cellRef = `${columnIndexToLetter(colIndex)}${rowIndex + 1}`;
            const value = cell.value;
            const explicitType = cell.type;

            // Style: 0 = normal, 1 = bold (header), 2 = date format, 3 = money format
            const isHeader = rowIndex === 0;

            if (value === null || value === undefined || value === '') {
                cells += `<c r="${cellRef}"><v></v></c>`;
            } else if (value instanceof Date) {
                // Date object - always treat as date
                const serial = dateToExcelSerial(value);
                cells += `<c r="${cellRef}" s="2"><v>${serial}</v></c>`;
            } else if (explicitType === 'date') {
                // Explicit date type with string/number value
                const dateValue = new Date(value as string | number);
                const serial = dateToExcelSerial(dateValue);
                cells += `<c r="${cellRef}" s="2"><v>${serial}</v></c>`;
            } else if (explicitType === 'money') {
                // Money type - format as currency with style 3
                const numValue = typeof value === 'number' ? value : parseFloat(String(value));
                if (!isNaN(numValue)) {
                    cells += `<c r="${cellRef}" s="3"><v>${numValue}</v></c>`;
                } else {
                    cells += `<c r="${cellRef}" s="3"><v>0</v></c>`;
                }
            } else if (typeof value === 'number') {
                // Number value - always treat as number
                cells += `<c r="${cellRef}"${isHeader ? ' s="1"' : ''}><v>${value}</v></c>`;
            } else if (explicitType === 'number') {
                // Explicit number type - parse the value
                const numValue = parseFloat(String(value));
                if (!isNaN(numValue)) {
                    cells += `<c r="${cellRef}"${isHeader ? ' s="1"' : ''}><v>${numValue}</v></c>`;
                } else {
                    // Failed to parse as number, treat as string
                    const strValue = String(value);
                    const stringIndex = sharedStrings.get(strValue) ?? 0;
                    cells += `<c r="${cellRef}" t="s"${isHeader ? ' s="1"' : ''}><v>${stringIndex}</v></c>`;
                }
            } else {
                // String - use shared strings table
                const strValue = String(value);
                const stringIndex = sharedStrings.get(strValue) ?? 0;
                cells += `<c r="${cellRef}" t="s"${isHeader ? ' s="1"' : ''}><v>${stringIndex}</v></c>`;
            }
        });
        rows += `<row r="${rowIndex + 1}">${cells}</row>\n`;
    });

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetViews>
    <sheetView workbookViewId="0" tabSelected="1"/>
  </sheetViews>
  ${colsXml}
  <sheetData>
${rows}  </sheetData>
</worksheet>`;
}

export async function exportToXlsx(config: XlsxConfig): Promise<void> {
    const { filename, sheet } = config;
    const { data } = sheet;

    // Collect all strings for the shared strings table
    // Only include values that will actually be written as strings
    const allStrings: string[] = [];
    data.forEach(row => {
        row.forEach(cell => {
            const value = cell.value;
            const explicitType = cell.type;

            // Skip empty values
            if (value === null || value === undefined || value === '') return;

            // Skip Date objects
            if (value instanceof Date) return;

            // Skip explicit date type
            if (explicitType === 'date') return;

            // Skip money type (stored as number)
            if (explicitType === 'money') return;

            // Skip number values
            if (typeof value === 'number') return;

            // Skip explicit number type that can be parsed
            if (explicitType === 'number' && !isNaN(parseFloat(String(value)))) return;

            // Everything else is a string
            allStrings.push(String(value));
        });
    });

    // Build shared strings map (string -> index)
    const uniqueStrings = [...new Set(allStrings)];
    const sharedStringsMap = new Map<string, number>();
    uniqueStrings.forEach((str, index) => {
        sharedStringsMap.set(str, index);
    });

    // Create ZIP file
    const zip = new JSZip();

    zip.file('[Content_Types].xml', generateContentTypes());
    zip.file('_rels/.rels', generateRels());
    zip.file('xl/workbook.xml', generateWorkbook());
    zip.file('xl/_rels/workbook.xml.rels', generateWorkbookRels());
    zip.file('xl/styles.xml', generateStyles());
    zip.file('xl/sharedStrings.xml', generateSharedStrings(allStrings));
    zip.file('xl/worksheets/sheet1.xml', generateSheet(data, sharedStringsMap));

    // Generate and download
    const blob = await zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export default exportToXlsx;
